import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import {
  heroSlidesQueryOptions,
  SLIDE_EFFECTS,
  SLIDE_COLOR_DEFAULTS,
  GLASS_ALIGNMENTS,
  GLASS_SIZES,
  type GlassAlignment,
  type GlassSize,
  type HeroSlideRow,
  type SlideEffect,
} from "@/lib/slides.queries";
import {
  createHeroSlide,
  deleteHeroSlide,
  reorderHeroSlides,
  updateHeroSlide,
} from "@/lib/slides.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, GripVertical, Sparkles, Library, Images } from "lucide-react";
import { FormDialog, FormSection, SaveButton, useDirty } from "@/components/admin/FormDialog";
import { AIGenerateImageDialog } from "@/components/admin/AIGenerateImageDialog";
import { MediaPickerDialog } from "@/components/admin/MediaPickerDialog";
import { MEDIA_TARGETS } from "@/lib/media-buckets";
import { useDirection } from "@/lib/direction";

export const Route = createFileRoute("/_authenticated/admin/slideshow")({
  component: SlideshowAdmin,
});

type FormState = {
  id?: string;
  kicker: string;
  title: string;
  subtitle: string;
  /** Full `/media/...` or external URL — the only image column the admin writes. */
  image_url: string;
  cta_primary_label: string;
  cta_primary_href: string;
  cta_secondary_label: string;
  cta_secondary_href: string;
  effect: SlideEffect;
  glass_alignment: GlassAlignment | null;
  image_position: GlassAlignment | null;
  glass_size: GlassSize | null;
  /** Hex colour of the frosted text panel behind the slide copy. */
  glass_bg_color: string;
  /** Panel opacity, integer 0-100. */
  glass_bg_opacity: number;
  kicker_color: string;
  title_color: string;
  subtitle_color: string;
  cta_primary_bg: string;
  cta_primary_fg: string;
  cta_secondary_bg: string;
  cta_secondary_fg: string;
  is_active: boolean;
  display_order: number;
};

const emptyForm: FormState = {
  kicker: "",
  title: "",
  subtitle: "",
  image_url: "",
  cta_primary_label: "",
  cta_primary_href: "",
  cta_secondary_label: "",
  cta_secondary_href: "",
  effect: "kenburns",
  glass_alignment: null,
  image_position: null,
  glass_size: null,
  glass_bg_color: SLIDE_COLOR_DEFAULTS.glass_bg_color,
  glass_bg_opacity: SLIDE_COLOR_DEFAULTS.glass_bg_opacity,
  kicker_color: SLIDE_COLOR_DEFAULTS.kicker_color,
  title_color: SLIDE_COLOR_DEFAULTS.title_color,
  subtitle_color: SLIDE_COLOR_DEFAULTS.subtitle_color,
  cta_primary_bg: SLIDE_COLOR_DEFAULTS.cta_primary_bg,
  cta_primary_fg: SLIDE_COLOR_DEFAULTS.cta_primary_fg,
  cta_secondary_bg: SLIDE_COLOR_DEFAULTS.cta_secondary_bg,
  cta_secondary_fg: SLIDE_COLOR_DEFAULTS.cta_secondary_fg,
  is_active: true,
  display_order: 0,
};

/** Clamp to an integer 0-100 so the payload never carries floats or out-of-range values. */
function clampOpacity(n: number): number {
  if (!Number.isFinite(n)) return SLIDE_COLOR_DEFAULTS.glass_bg_opacity;
  return Math.min(100, Math.max(0, Math.round(n)));
}

/** "#rrggbb" (or "#rgb") + 0-100 -> "rgba(r, g, b, a)" for the live swatch. */
function hexToRgba(hex: string, opacityPct: number): string {
  let h = (hex || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) h = SLIDE_COLOR_DEFAULTS.glass_bg_color.slice(1);
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${clampOpacity(opacityPct) / 100})`;
}

function SlideshowAdmin() {
  const { data: rows } = useQuery(heroSlidesQueryOptions());
  const qc = useQueryClient();
  const create = useServerFn(createHeroSlide);
  const update = useServerFn(updateHeroSlide);
  const del = useServerFn(deleteHeroSlide);
  const reorder = useServerFn(reorderHeroSlides);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [aiOpen, setAiOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const { direction } = useDirection();
  const dirty = useDirty(form, open);

  const sorted = useMemo(
    () =>
      [...(rows ?? [])].sort(
        (a, b) =>
          a.display_order - b.display_order ||
          a.created_at.localeCompare(b.created_at),
      ),
    [rows],
  );

  const [localOrder, setLocalOrder] = useState<HeroSlideRow[]>(sorted);
  // Keep the drag-reorder copy in sync with the server data. Two cases:
  // a slide was added/removed -> adopt the server list wholesale; contents
  // changed on an existing slide -> refresh each row IN PLACE, preserving a
  // possibly mid-drag local order. Syncing only on id changes (the previous
  // behavior) left stale rows behind after every edit, so reopening a slide
  // showed its pre-save values until a full page reload.
  useEffect(() => {
    setLocalOrder((prev) => {
      const byId = new Map(sorted.map((r) => [r.id, r]));
      const sameIdSet =
        prev.length === sorted.length && prev.every((r) => byId.has(r.id));
      if (!sameIdSet) return sorted;
      return prev.map((r) => byId.get(r.id) ?? r);
    });
  }, [sorted]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const invalidate = () => qc.invalidateQueries({ queryKey: ["hero-slides"] });

  const saveMut = useMutation({
    mutationFn: async (data: FormState) => {
      const payload = {
        ...data,
        // A stray space must not become a broken link; blank is stored as NULL.
        cta_primary_href: data.cta_primary_href.trim(),
        cta_secondary_href: data.cta_secondary_href.trim(),
        glass_bg_color: data.glass_bg_color || SLIDE_COLOR_DEFAULTS.glass_bg_color,
        glass_bg_opacity: clampOpacity(data.glass_bg_opacity),
      };
      if (data.id) return update({ data: { ...payload, id: data.id } });
      return create({ data: payload });
    },
    onSuccess: () => {
      toast.success("Slide saved");
      setOpen(false);
      setForm(emptyForm);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to save"),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Slide deleted");
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to delete"),
  });

  const persistOrder = useMutation({
    mutationFn: async (ordered: HeroSlideRow[]) =>
      reorder({
        data: {
          items: ordered.map((r, i) => ({ id: r.id, display_order: (i + 1) * 10 })),
        },
      }),
    onSuccess: () => {
      toast.success("Slide order updated");
      invalidate();
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Reorder failed");
      setLocalOrder(sorted);
    },
  });

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localOrder.findIndex((x) => x.id === active.id);
    const newIndex = localOrder.findIndex((x) => x.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(localOrder, oldIndex, newIndex);
    setLocalOrder(next);
    persistOrder.mutate(next);
  }

  const openNew = () => {
    setForm({ ...emptyForm, display_order: (localOrder.length + 1) * 10 });
    setOpen(true);
  };

  const openEdit = (r: HeroSlideRow) => {
    setForm({
      id: r.id,
      kicker: r.kicker ?? "",
      title: r.title ?? "",
      subtitle: r.subtitle ?? "",
      image_url: r.image_url ?? "",
      cta_primary_label: r.cta_primary_label ?? "",
      cta_primary_href: r.cta_primary_href ?? "",
      cta_secondary_label: r.cta_secondary_label ?? "",
      cta_secondary_href: r.cta_secondary_href ?? "",
      effect: r.effect ?? "kenburns",
      glass_alignment: r.glass_alignment ?? null,
      image_position: r.image_position ?? null,
      glass_size: r.glass_size ?? null,
      glass_bg_color: r.glass_bg_color ?? SLIDE_COLOR_DEFAULTS.glass_bg_color,
      glass_bg_opacity: r.glass_bg_opacity ?? SLIDE_COLOR_DEFAULTS.glass_bg_opacity,
      kicker_color: r.kicker_color ?? SLIDE_COLOR_DEFAULTS.kicker_color,
      title_color: r.title_color ?? SLIDE_COLOR_DEFAULTS.title_color,
      subtitle_color: r.subtitle_color ?? SLIDE_COLOR_DEFAULTS.subtitle_color,
      cta_primary_bg: r.cta_primary_bg ?? SLIDE_COLOR_DEFAULTS.cta_primary_bg,
      cta_primary_fg: r.cta_primary_fg ?? SLIDE_COLOR_DEFAULTS.cta_primary_fg,
      cta_secondary_bg: r.cta_secondary_bg ?? SLIDE_COLOR_DEFAULTS.cta_secondary_bg,
      cta_secondary_fg: r.cta_secondary_fg ?? SLIDE_COLOR_DEFAULTS.cta_secondary_fg,
      is_active: r.is_active,
      display_order: r.display_order,
    });
    setOpen(true);
  };

  const onSubmit = () => {
    if (!form.image_url) {
      toast.error("Please add an image");
      return;
    }
    saveMut.mutate(form);
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>Hero Slideshow</CardTitle>
          <CardDescription>
            {sorted.filter((r) => r.is_active).length} active / {sorted.length} total. Slides appear in
            display-order on the homepage hero.
          </CardDescription>
        </div>
        <Button size="sm" onClick={openNew}>
          <Plus className="mr-1 h-4 w-4" /> New slide
        </Button>
        {/* While the AI/library picker (a sibling modal) is up, Radix reports
            clicks in it as outside-interactions here — `busy` keeps this dialog
            open so the draft survives. */}
        <FormDialog
          open={open}
          onOpenChange={setOpen}
          size="lg"
          icon={Images}
          title={form.id ? "Edit slide" : "New slide"}
          context={form.id ? form.title || form.kicker || "Untitled slide" : "New homepage hero slide"}
          dirty={dirty}
          busy={aiOpen || libraryOpen || saveMut.isPending}
          asForm
          onSubmit={onSubmit}
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <SaveButton dirty={dirty} pending={saveMut.isPending} label="Save slide" />
            </>
          }
        >
          <div className="space-y-4">
<FormSection title="Image" cols={1}>
              {/* Image */}
              <div className="grid gap-2">
                <Label>Image</Label>
                {form.image_url ? (
                  <div className="relative overflow-hidden rounded-md border bg-muted">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={form.image_url}
                      alt="Slide preview"
                      className="max-h-96 w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="grid h-48 place-items-center rounded-md border border-dashed text-xs text-muted-foreground">
                    No image yet
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setLibraryOpen(true)}
                  >
                    <Library className="mr-1 h-4 w-4" /> Upload/Library
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAiOpen(true)}
                  >
                    <Sparkles className="mr-1 h-4 w-4" /> AI Generate
                  </Button>
                </div>
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  id="image_url"
                  type="text"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="…or paste an image URL (https://… or /media/…)"
                />
              </div>

</FormSection>
<FormSection title="Text" cols={1}>
              {/* Text fields */}
              <div className="grid gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Top text color"
                    title="Top text color"
                    value={form.kicker_color || SLIDE_COLOR_DEFAULTS.kicker_color}
                    onChange={(e) => setForm({ ...form, kicker_color: e.target.value })}
                    className="h-7 w-7 shrink-0 cursor-pointer rounded border p-0"
                  />
                  <Label htmlFor="kicker" className="m-0">Top text (next to blue line)</Label>
                </div>
                <Input
                  id="kicker"
                  value={form.kicker}
                  onChange={(e) => setForm({ ...form, kicker: e.target.value })}
                  placeholder="e.g. Medical Insight"
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Title color"
                    title="Title color"
                    value={form.title_color || SLIDE_COLOR_DEFAULTS.title_color}
                    onChange={(e) => setForm({ ...form, title_color: e.target.value })}
                    className="h-7 w-7 shrink-0 cursor-pointer rounded border p-0"
                  />
                  <Label htmlFor="title" className="m-0">Title</Label>
                </div>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Main headline"
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Description color"
                    title="Description color"
                    value={form.subtitle_color || SLIDE_COLOR_DEFAULTS.subtitle_color}
                    onChange={(e) => setForm({ ...form, subtitle_color: e.target.value })}
                    className="h-7 w-7 shrink-0 cursor-pointer rounded border p-0"
                  />
                  <Label htmlFor="subtitle" className="m-0">Description (sub-text)</Label>
                </div>
                <Textarea
                  id="subtitle"
                  rows={3}
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  placeholder="Short supporting paragraph"
                />
              </div>

</FormSection>
<FormSection title="Buttons" cols={1}>
              {/* CTAs */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-md border p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Button 1 (dark)
                    </p>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                        BG
                        <input
                          type="color"
                          aria-label="Button 1 background"
                          value={form.cta_primary_bg || SLIDE_COLOR_DEFAULTS.cta_primary_bg}
                          onChange={(e) => setForm({ ...form, cta_primary_bg: e.target.value })}
                          className="h-6 w-6 cursor-pointer rounded border p-0"
                        />
                      </label>
                      <label className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                        FG
                        <input
                          type="color"
                          aria-label="Button 1 foreground"
                          value={form.cta_primary_fg || SLIDE_COLOR_DEFAULTS.cta_primary_fg}
                          onChange={(e) => setForm({ ...form, cta_primary_fg: e.target.value })}
                          className="h-6 w-6 cursor-pointer rounded border p-0"
                        />
                      </label>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="cta_primary_label">Button 1 label</Label>
                    <Input
                      id="cta_primary_label"
                      value={form.cta_primary_label}
                      onChange={(e) => setForm({ ...form, cta_primary_label: e.target.value })}
                      placeholder="e.g. Read the story"
                    />
                    <Label htmlFor="cta_primary_href">Button 1 target</Label>
                    <Input
                      id="cta_primary_href"
                      value={form.cta_primary_href}
                      onChange={(e) => setForm({ ...form, cta_primary_href: e.target.value })}
                      placeholder="URL or /path"
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave blank to show the button disabled.
                    </p>
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Button 2 (light)
                    </p>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                        BG
                        <input
                          type="color"
                          aria-label="Button 2 background"
                          value={form.cta_secondary_bg || SLIDE_COLOR_DEFAULTS.cta_secondary_bg}
                          onChange={(e) => setForm({ ...form, cta_secondary_bg: e.target.value })}
                          className="h-6 w-6 cursor-pointer rounded border p-0"
                        />
                      </label>
                      <label className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                        FG
                        <input
                          type="color"
                          aria-label="Button 2 foreground"
                          value={form.cta_secondary_fg || SLIDE_COLOR_DEFAULTS.cta_secondary_fg}
                          onChange={(e) => setForm({ ...form, cta_secondary_fg: e.target.value })}
                          className="h-6 w-6 cursor-pointer rounded border p-0"
                        />
                      </label>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="cta_secondary_label">Button 2 label</Label>
                    <Input
                      id="cta_secondary_label"
                      value={form.cta_secondary_label}
                      onChange={(e) => setForm({ ...form, cta_secondary_label: e.target.value })}
                      placeholder="e.g. Learn more"
                    />
                    <Label htmlFor="cta_secondary_href">Button 2 target</Label>
                    <Input
                      id="cta_secondary_href"
                      value={form.cta_secondary_href}
                      onChange={(e) => setForm({ ...form, cta_secondary_href: e.target.value })}
                      placeholder="URL or /path"
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave blank to show the button disabled.
                    </p>
                  </div>
                </div>
              </div>

</FormSection>
<FormSection title="Layout" cols={1}>
              {/* Effect */}
              <div className="grid gap-2">
                <Label>Image effect</Label>
                <Select
                  value={form.effect}
                  onValueChange={(v) => setForm({ ...form, effect: v as SlideEffect })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SLIDE_EFFECTS.map((e) => (
                      <SelectItem key={e.value} value={e.value}>
                        {e.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Image focus position */}
              <div className="grid gap-2">
                <Label>Image focus position</Label>
                <div className="grid w-fit grid-cols-3 gap-1 rounded-md border p-1">
                  {GLASS_ALIGNMENTS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      title={a.replace("-", " ")}
                      aria-label={`Start image at ${a.replace("-", " ")}`}
                      aria-pressed={form.image_position === a}
                      onClick={() =>
                        setForm({
                          ...form,
                          image_position: form.image_position === a ? null : a,
                        })
                      }
                      className={`flex h-8 w-8 items-center justify-center rounded-sm transition-colors ${
                        form.image_position === a
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <span className="h-2 w-2 rounded-[2px] bg-current" />
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Which part of the image the hero keeps in view. Zoom and Ken Burns effects
                  stay anchored here for the whole animation; pans start here and drift
                  slightly sideways. Click the active cell again to clear (default: top
                  center).
                </p>
              </div>

              {/* Glass panel alignment */}
              <div className="grid gap-2">
                <Label>Text panel position</Label>
                <div className="grid w-fit grid-cols-3 gap-1 rounded-md border p-1">
                  {GLASS_ALIGNMENTS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      title={a.replace("-", " ")}
                      aria-label={`Align ${a.replace("-", " ")}`}
                      aria-pressed={form.glass_alignment === a}
                      onClick={() =>
                        setForm({
                          ...form,
                          glass_alignment: form.glass_alignment === a ? null : a,
                        })
                      }
                      className={`flex h-8 w-8 items-center justify-center rounded-sm transition-colors ${
                        form.glass_alignment === a
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <span className="h-2 w-2 rounded-[2px] bg-current" />
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Where the frosted text panel sits on the slide. Click the active cell again to
                  clear it and use the theme default (bottom left).
                </p>
              </div>

              {/* Text panel background colour + opacity */}
              <div className="grid gap-2">
                <Label>Text panel background</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Text panel background color"
                    title="Text panel background color"
                    value={form.glass_bg_color || SLIDE_COLOR_DEFAULTS.glass_bg_color}
                    onChange={(e) => setForm({ ...form, glass_bg_color: e.target.value })}
                    className="h-7 w-7 shrink-0 cursor-pointer rounded border p-0"
                  />
                  <span className="font-mono text-xs text-muted-foreground">
                    {(form.glass_bg_color || SLIDE_COLOR_DEFAULTS.glass_bg_color).toLowerCase()}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Label htmlFor="glass_bg_opacity" className="m-0 w-16 shrink-0 text-xs text-muted-foreground">
                    Opacity
                  </Label>
                  <Slider
                    id="glass_bg_opacity"
                    aria-label="Text panel opacity"
                    value={[form.glass_bg_opacity]}
                    min={0}
                    max={100}
                    step={1}
                    onValueChange={([v]) => setForm({ ...form, glass_bg_opacity: clampOpacity(v) })}
                    className="flex-1"
                  />
                  <span className="w-12 text-right font-mono text-xs text-muted-foreground">
                    {clampOpacity(form.glass_bg_opacity)}%
                  </span>
                </div>
                <div
                  aria-hidden
                  className="relative h-10 overflow-hidden rounded-md border"
                  style={{
                    backgroundImage:
                      "linear-gradient(135deg, #0ea5e9 0%, #6366f1 25%, #f97316 50%, #22c55e 75%, #0f172a 100%)",
                  }}
                >
                  <div
                    className="absolute inset-1 rounded-sm backdrop-blur-sm"
                    style={{ backgroundColor: hexToRgba(form.glass_bg_color, form.glass_bg_opacity) }}
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    Colour and transparency of the frosted panel behind the slide text. Default is
                    white at 20%.
                  </p>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto shrink-0 px-0 text-xs"
                    onClick={() =>
                      setForm({
                        ...form,
                        glass_bg_color: SLIDE_COLOR_DEFAULTS.glass_bg_color,
                        glass_bg_opacity: SLIDE_COLOR_DEFAULTS.glass_bg_opacity,
                      })
                    }
                  >
                    Reset to default
                  </Button>
                </div>
              </div>

              {/* Text panel size */}
              <div className="grid gap-2">
                <Label>Text panel size</Label>
                <div className="flex w-fit gap-1 rounded-md border p-1">
                  {GLASS_SIZES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      aria-pressed={form.glass_size === s.value}
                      onClick={() =>
                        setForm({
                          ...form,
                          glass_size: form.glass_size === s.value ? null : s.value,
                        })
                      }
                      className={`rounded-sm px-3 py-1.5 text-sm transition-colors ${
                        form.glass_size === s.value
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  How wide the text panel is on desktop (phones always use the full width).
                  Click the active size again to clear it and use the default (Large).
                </p>
              </div>

</FormSection>
<FormSection title="Visibility" cols={1}>
              {/* Active */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                  id="is_active"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

</FormSection>
          </div>
        </FormDialog>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
              <TableHead className="w-10"></TableHead>
                <TableHead className="w-20">Image</TableHead>
                <TableHead>Title / kicker</TableHead>
                <TableHead className="hidden md:table-cell">Effect</TableHead>
                <TableHead className="w-20">Active</TableHead>
                <TableHead className="w-28 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localOrder.map((r) => r.id)}
                strategy={verticalListSortingStrategy}
              >
                <TableBody>
                  {localOrder.length === 0 && (
                    <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                        No slides yet — click <strong>New slide</strong> to add one.
                      </TableCell>
                    </TableRow>
                  )}
                  {localOrder.map((r) => (
                    <SortableSlideRow
                      key={r.id}
                      row={r}
                      onEdit={() => openEdit(r)}
                      onDelete={() => {
                        if (confirm(`Delete slide "${r.title || r.kicker}"?`))
                          delMut.mutate(r.id);
                      }}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </DndContext>
          </Table>
        </div>
      </CardContent>
      <AIGenerateImageDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        defaultDirection={direction}
        onSelect={({ url }) => {
          setForm((f) => ({ ...f, image_url: url }));
          toast.success("AI image attached to slide");
        }}
      />
      {/* Slides are referenced by their permanent URL alone; the legacy
          image_path column is no longer written (migration 023 nulls it). */}
      <MediaPickerDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        target={MEDIA_TARGETS.heroSlide}
        browseFolder="all"
        title="Choose a slide image"
        onSelect={({ url }) => {
          setForm((f) => ({ ...f, image_url: url }));
          toast.success("Image attached to slide");
        }}
      />
    </Card>
  );
}

function SortableSlideRow({
  row: r,
  onEdit,
  onDelete,
}: {
  row: HeroSlideRow;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: r.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "hsl(var(--secondary))" : undefined,
  };
  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell>
        <button
          type="button"
          className="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-secondary active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell>
        {r.image_url ? (
          <img src={r.image_url} alt="" className="h-10 w-16 rounded object-cover" />
        ) : (
          <div className="h-10 w-16 rounded bg-muted" />
        )}
      </TableCell>
      <TableCell>
        <div className="font-medium leading-tight">
          {r.title || <em className="text-muted-foreground">Untitled</em>}
        </div>
        <div className="text-xs text-muted-foreground">{r.kicker}</div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <Badge variant="secondary">{r.effect}</Badge>
      </TableCell>
      <TableCell>
        {r.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Hidden</Badge>}
      </TableCell>
      <TableCell className="text-right">
        <Button size="icon" variant="ghost" onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" onClick={onDelete}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
